import React from 'react';
import { Card, Table, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { useClientCreditSummaryQuery } from '../../queries/credit.query';
import { useVoidCreditMutation } from '../../mutations/credit.mutation';
import { useQueryClient } from '@tanstack/react-query';
import AddCreditModal from './AddCreditModal';

interface ClientCreditSummaryProps {
  clientId: string;
  clientName: string;
}

function ClientCreditSummary({ clientId, clientName }: ClientCreditSummaryProps) {
  const queryClient = useQueryClient();
  const { data: summary, isLoading, isError, refetch } = useClientCreditSummaryQuery(clientId);
  const { mutate: voidCredit, isLoading: isVoiding } = useVoidCreditMutation();

  const handleVoidCredit = (creditId: string) => {
    if (window.confirm('Are you sure you want to void this credit?')) {
      voidCredit(creditId, {
        onSuccess: () => {
          queryClient.invalidateQueries(['client-credit-summary', clientId]);
        }
      });
    }
  };

  const handleAddCreditSuccess = () => {
    refetch();
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Customer Credits</h5>
        </Card.Header>
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" /> Loading credit information...
        </Card.Body>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Customer Credits</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">Failed to load credit information</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Customer Credits</h5>
        <AddCreditModal
          clientId={clientId}
          clientName={clientName}
          onSuccess={handleAddCreditSuccess}
        />
      </Card.Header>
      <Card.Body>
        <div className="d-flex justify-content-around mb-3 text-center">
          <div>
            <h3 className="text-success mb-0">{formatCurrency(summary?.availableBalance ?? 0)}</h3>
            <small className="text-muted">Available Balance</small>
          </div>
          <div>
            <h5 className="text-muted mb-0">{formatCurrency(summary?.totalAdded ?? 0)}</h5>
            <small className="text-muted">Total Added</small>
          </div>
          <div>
            <h5 className="text-muted mb-0">{formatCurrency(summary?.totalSpent ?? 0)}</h5>
            <small className="text-muted">Total Spent</small>
          </div>
        </div>

        {summary?.credits && summary.credits.length > 0 ? (
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Date Added</th>
                <th>Original Amount</th>
                <th>Remaining</th>
                <th>Note</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {summary.credits.map((credit) => (
                <tr key={credit.id}>
                  <td>{formatDate(credit.insertedAt)}</td>
                  <td>{formatCurrency(credit.amount)}</td>
                  <td>{formatCurrency(credit.remainingAmount)}</td>
                  <td>{credit.note || '-'}</td>
                  <td>
                    {credit.voidedAt ? (
                      <Badge bg="secondary">Voided</Badge>
                    ) : credit.remainingAmount === 0 ? (
                      <Badge bg="info">Fully Spent</Badge>
                    ) : (
                      <Badge bg="success">Active</Badge>
                    )}
                    {credit.notificationSent && (
                      <Badge bg="light" text="dark" className="ms-1">
                        Notified
                      </Badge>
                    )}
                  </td>
                  <td>
                    {!credit.voidedAt && credit.remainingAmount > 0 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={isVoiding}
                        onClick={() => handleVoidCredit(credit.id)}>
                        Void
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-muted text-center mb-0">No credits on record</p>
        )}
      </Card.Body>
    </Card>
  );
}

export default ClientCreditSummary;
